import { useEffect, useMemo, useRef, useState } from "react";
import { Container } from "react-bootstrap";
import PageLoading from "components/PageLoading";
import ExtractTable from "./ExtractTable/ExtractTable";
import PaginationControls from "../Analysis/ComponentsAnalysis/PaginationControls/PaginationControls";
import SearchExport from "../Analysis/ComponentsAnalysis/SearchExport/SearchExport";
import FilterSectionExtract from "./FilterSectionExtract/FilterSectionExtract";
import HistoryExtract from "./HistoryExtract/HistoryExtract";
import "./Extract.css";
import "../Analysis/ComponentsAnalysis/PaginationControls/PaginationControls.css";
import {
  getExtratoSolicitacoesByAno,
  getExtratoSolicitacoesLegacy,
  getExtratoEventosBySolicitacao,
  getHistoricoAcaoExtrato,
} from "../../../services/ExtractService";
import {
  formatDateTimeExtratoList,
  formatHistoricoDocumentacaoDataHora,
} from "../../../utils/date";
import type { ExtractHistoryEntry, ExtractRow } from "./shared/extractListTypes";
import {
  onlyDigits,
  rowSituacaoExtratoMatchesFilters,
} from "./shared/extractListUtils";
import { mapExtratoSolicitacoesToRows } from "./V2/mapExtratoSolicitacoesToRows";
import { getAnoRequestParam } from "./V2/anoRequestParam";

const STORAGE_KEY = "extract_filters_v2";

const readStoredYear = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return 2026;
    const parsed = JSON.parse(raw);
    return Number.isFinite(parsed?.selectedYear) ? parsed.selectedYear : 2026;
  } catch {
    return 2026;
  }
};

const ALL_SITUATIONS = [
  "Análise não finalizada",
  "Extrato Pendente",
  "Extrato Enviado",
  "Extrato Recusado",
  "Extrato Validado",
  "Inapto",
];

type SortConfig = {
  key: string | null;
  direction: "asc" | "desc" | null;
};

const Extract = () => {
  const [data, setData] = useState<ExtractRow[]>([]);
  const [availableYears, setAvailableYears] = useState<number[]>([2026, 2025]);
  const [selectedYear, setSelectedYear] = useState(() => readStoredYear());
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: null,
    direction: null,
  });
  const [selectedSituation, setSelectedSituation] =
    useState<string[]>(ALL_SITUATIONS);
  const [searchTerm, setSearchTerm] = useState("");
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyDataExtract, setHistoryDataExtract] = useState<
    ExtractHistoryEntry[]
  >([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const skipNextYearFetchRef = useRef(false);

  const extratoPorSolicitacao = Number(selectedYear) > 2025;

  const extractAnosModelo = (
    response:
      | {
          avaliacoesModelo?: { anoReferencia: number }[];
          extratosModelo?: { anoReferencia: number }[];
        }
      | null
      | undefined,
  ): number[] => {
    const anos =
      response?.avaliacoesModelo?.map((m) => m.anoReferencia) ??
      response?.extratosModelo?.map((m) => m.anoReferencia) ??
      [];
    return [...new Set(anos)].sort((a, b) => b - a);
  };

  const loadRowsForYear = async (ano: number) => {
    if (ano === 2025) {
      const extratoResponse = await getExtratoSolicitacoesLegacy();
      const solicitacoes = extratoResponse?.solicitacoes ?? [];
      return mapExtratoSolicitacoesToRows(solicitacoes, 2025);
    }

    const extratoResponse = await getExtratoSolicitacoesByAno(
      getAnoRequestParam(ano),
    );
    const solicitacoes = extratoResponse?.solicitacoes ?? [];
    const anosModelo = extractAnosModelo(extratoResponse);
    if (anosModelo.length > 0) {
      setAvailableYears(anosModelo);
    }

    return mapExtratoSolicitacoesToRows(solicitacoes, ano);
  };

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;

      const parsed = JSON.parse(raw);

      if (Array.isArray(parsed?.selectedSituation)) {
        const stored = parsed.selectedSituation.filter(
          (s: unknown) => typeof s === "string",
        ) as string[];
        const sanitized = stored.filter((s) => ALL_SITUATIONS.includes(s));
        const newlyAdded = ALL_SITUATIONS.filter((s) => !stored.includes(s));
        const result = [...sanitized, ...newlyAdded];
        setSelectedSituation(result.length > 0 ? result : ALL_SITUATIONS);
      }

      if (Number.isFinite(parsed?.selectedYear)) {
        setSelectedYear(parsed.selectedYear);
      }

      if (typeof parsed?.searchTerm === "string") {
        setSearchTerm(parsed.searchTerm);
      }

      if (Number.isFinite(parsed?.currentPage)) {
        setCurrentPage(parsed.currentPage);
      }

      if (Number.isFinite(parsed?.itemsPerPage)) {
        setItemsPerPage(parsed.itemsPerPage);
      }

      if (parsed?.sortConfig && typeof parsed.sortConfig === "object") {
        const sc = parsed.sortConfig as {
          key?: string | null;
          direction?: string | null;
        };
        setSortConfig({
          key: sc.key ?? null,
          direction:
            sc.direction === "asc" || sc.direction === "desc"
              ? sc.direction
              : null,
        });
      }
    } catch (e) {
      console.warn("⚠️ Não foi possível restaurar filtros do extrato:", e);
    }
  }, []);

  useEffect(() => {
    try {
      const payload = {
        selectedSituation,
        selectedYear,
        searchTerm,
        currentPage,
        itemsPerPage,
        sortConfig,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (e) {
      console.warn("⚠️ Não foi possível salvar filtros do extrato:", e);
    }
  }, [
    selectedSituation,
    selectedYear,
    searchTerm,
    currentPage,
    itemsPerPage,
    sortConfig,
  ]);

  useEffect(() => {
    return () => {
      localStorage.removeItem(STORAGE_KEY);
    };
  }, []);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        let anoSelecionado = readStoredYear();

        if (anoSelecionado > 2025) {
          try {
            const extratoResponse = await getExtratoSolicitacoesByAno(
              getAnoRequestParam(anoSelecionado),
            );
            anoSelecionado =
              extratoResponse?.anoReferenciaSelecionado ?? anoSelecionado;
            const anosDisponiveis = extractAnosModelo(extratoResponse);
            if (anosDisponiveis.length > 0) {
              setAvailableYears(anosDisponiveis);
            }
          } catch {
            setAvailableYears([2026, 2025]);
          }
        } else {
          setAvailableYears([2026, 2025]);
        }

        skipNextYearFetchRef.current = true;
        setSelectedYear(anoSelecionado);
        setData(await loadRowsForYear(anoSelecionado));
      } catch (error) {
        console.error("❌ Erro ao buscar dados do extrato:", error);
        setData([]);
      } finally {
        setLoading(false);
        setInitialLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  useEffect(() => {
    if (initialLoading || !selectedYear) return;
    if (skipNextYearFetchRef.current) {
      skipNextYearFetchRef.current = false;
      return;
    }

    const fetchDataByYear = async () => {
      setLoading(true);
      try {
        setData(await loadRowsForYear(selectedYear));
      } catch (error) {
        console.error("❌ Erro ao buscar extrato por ano:", error);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDataByYear();
  }, [selectedYear, initialLoading]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedYear, selectedSituation, itemsPerPage, searchTerm]);

  const filteredData = useMemo(() => {
    const termo = searchTerm.trim().toLowerCase();
    const termoDigits = onlyDigits(searchTerm);

    return data.filter((item) => {
      const matchesYear = Number(item.ano) === Number(selectedYear);
      const isSituationMatch = rowSituacaoExtratoMatchesFilters(
        item.situacao,
        selectedSituation,
        ALL_SITUATIONS,
      );

      let matchesSearch = true;
      if (termo || termoDigits) {
        const siglaOk = termo
          ? (item.sigla || "").toLowerCase().includes(termo)
          : false;
        const cnpjOk = termoDigits
          ? onlyDigits(item.cnpj).includes(termoDigits)
          : false;
        matchesSearch = siglaOk || cnpjOk;
      }

      return matchesYear && isSituationMatch && matchesSearch;
    });
  }, [data, selectedYear, selectedSituation, searchTerm]);

  const sortedData = useMemo(() => {
    const list = [...filteredData];
    const { key, direction } = sortConfig;
    if (!key || !direction) return list;

    const parseDateTs = (v: string | null | undefined): number | null => {
      if (!v || String(v).trim() === "") return null;
      const t = new Date(v).getTime();
      return Number.isNaN(t) ? null : t;
    };

    const getValue = (item: ExtractRow): string | number | null => {
      switch (key) {
        case "cnpj": {
          const d = onlyDigits(item.cnpj);
          return d || null;
        }
        case "sigla":
          return item.sigla ? item.sigla.toLowerCase() : null;
        case "solicitacao":
          return item.solicitacao || null;
        case "situacao":
          return item.situacao || null;
        case "envio":
          return parseDateTs(item.envio);
        case "atualizacao":
          return parseDateTs(item.atualizacao);
        default:
          return null;
      }
    };

    return list.sort((a, b) => {
      const aValue = getValue(a);
      const bValue = getValue(b);

      if (aValue === null && bValue === null) return 0;
      if (aValue === null) return 1;
      if (bValue === null) return -1;

      if (typeof aValue === "number" && typeof bValue === "number") {
        return direction === "asc" ? aValue - bValue : bValue - aValue;
      }

      return direction === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
  }, [filteredData, sortConfig]);

  const totalPages = Math.ceil(sortedData.length / itemsPerPage) || 1;
  const currentItems = sortedData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const startResult =
    sortedData.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endResult = Math.min(currentPage * itemsPerPage, sortedData.length);

  const handleSort = (key: string) => {
    let direction: "asc" | "desc" | null = "asc";
    let nextKey: string | null = key;

    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    } else if (sortConfig.key === key && sortConfig.direction === "desc") {
      direction = null;
      nextKey = null;
    }

    setSortConfig({ key: nextKey, direction });
  };

  const getSortIcon = (key: string) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === "asc"
        ? "▲"
        : sortConfig.direction === "desc"
          ? "▼"
          : "↕";
    }
    return "↕";
  };

  const handleSituationFilter = (value: string | string[]) => {
    if (Array.isArray(value)) {
      setSelectedSituation(value);
      return;
    }
    setSelectedSituation((prev) =>
      prev.includes(value) ? prev.filter((p) => p !== value) : [...prev, value],
    );
    setCurrentPage(1);
  };

  const isISO = (v: unknown): v is string =>
    typeof v === "string" && !Number.isNaN(Date.parse(v));

  const isEnvioDoExtrato = (txt: string | undefined) =>
    String(txt || "")
      .toLowerCase()
      .includes("envio do extrato");

  const mapHistoricoEntries = (historico: any[]): ExtractHistoryEntry[] =>
    historico.map((entry: any) => {
      const nomeAcao = entry?.name ?? entry?.nome ?? entry?.acao ?? "-";
      const dataUpload =
        entry?.dataUpload ??
        (isEnvioDoExtrato(nomeAcao) && isISO(entry?.dataHora)
          ? entry.dataHora
          : null);
      const isoParaExibir =
        entry?.dataHoraBrasilia ??
        entry?.dataHora ??
        entry?.dataUpload ??
        entry?.data ??
        entry?.criadoEm ??
        null;

      return {
        acao: nomeAcao,
        idExtrato: entry?.idExtrato ? String(entry.idExtrato) : undefined,
        identificadorBlob: entry?.identificadorBlob
          ? String(entry.identificadorBlob)
          : undefined,
        arquivoId: entry?.arquivoId != null ? Number(entry.arquivoId) : undefined,
        dataUpload,
        dataHora: entry?.dataHora ?? undefined,
        data: isoParaExibir
          ? formatHistoricoDocumentacaoDataHora(isoParaExibir)
          : "-",
      };
    });

  const handleOpenHistory = async (item: ExtractRow) => {
    setHistoryDataExtract([]);
    setShowHistoryModal(true);
    setLoadingHistory(true);

    try {
      if (extratoPorSolicitacao && item.solicitacaoId) {
        const eventos = await getExtratoEventosBySolicitacao(item.solicitacaoId);
        setHistoryDataExtract(
          mapHistoricoEntries(
            eventos.map((entry) => ({
              nome: entry?.nome ?? "-",
              dataHora: entry?.criadoEm ?? null,
              arquivoId: entry?.arquivoId ?? null,
            })),
          ),
        );
        return;
      }

      const idSindicato = item.idSindicato ?? item.id;
      if (!idSindicato) {
        setHistoryDataExtract([]);
        return;
      }

      const response = await getHistoricoAcaoExtrato(idSindicato);
      const historico = response?.historicoAcaoExtrato || [];
      setHistoryDataExtract(mapHistoricoEntries(historico));
    } catch (error) {
      console.error("❌ Erro ao carregar histórico do extrato:", error);
      setHistoryDataExtract([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleCloseHistory = () => {
    setShowHistoryModal(false);
    setHistoryDataExtract([]);
    setLoadingHistory(false);
  };

  const exportData = useMemo(
    () =>
      sortedData.map((item) => ({
        CNPJ: item.cnpj ?? "",
        SIGLA: item.sigla ?? "",
        SOLICITACAO: item.solicitacao ?? "",
        SITUACAO: item.situacao ?? "",
        ENVIO_EXTRATO: formatDateTimeExtratoList(item.envio),
        ULTIMA_ATUALIZACAO: formatDateTimeExtratoList(item.atualizacao),
      })),
    [sortedData],
  );

  if (initialLoading || loading) return <PageLoading />;

  return (
    <Container fluid className="extract-container">
      <h1 className="extract-title">GESTÃO DE EXTRATOS</h1>

      <div className="filters-actions-container shipping-filters-actions-container">
        <FilterSectionExtract
          selectedSituation={selectedSituation}
          handleSituationFilter={handleSituationFilter}
          allSituations={ALL_SITUATIONS}
        />

        <SearchExport
          selectedYear={selectedYear}
          setSelectedYear={setSelectedYear}
          availableYears={availableYears}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          exportData={exportData}
          fileName={`GestaoExtratos_${selectedYear}`}
        />
      </div>

      <div className="table-responsive-wrapper extract-table-scroll">
        <ExtractTable
          currentItems={currentItems}
          handleSort={handleSort}
          getSortIcon={getSortIcon}
          onOpenHistory={handleOpenHistory}
          mostrarColunaSolicitacao={extratoPorSolicitacao}
          selectedYear={selectedYear}
          formatDate={formatDateTimeExtratoList}
        />
      </div>

      <div className="pagination-wrapper">
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          setCurrentPage={setCurrentPage}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          startResult={startResult}
          endResult={endResult}
          totalItems={sortedData.length}
        />

        <HistoryExtract
          show={showHistoryModal}
          onHide={handleCloseHistory}
          historyDataExtract={historyDataExtract}
          loading={loadingHistory}
        />
      </div>
    </Container>
  );
};

export default Extract;
export type { ExtractHistoryEntry };
